// The TASK BOARD (N-BOARD-1 AC-1), recreated 1:1 from the prototype (screens.js tasks board + app.css
// .board/.col/.col-head): flat columns separated by space, a mono column head with its count, and the
// terminal columns collapsible from the toolbar (prototype .col-collapsed). The columns are DERIVED from the
// projection's selected grouping. State columns remain the exact lifecycle state enum the owning module
// returns (G-182); owner and KPI columns use stable ids internally while rendering only owning-module
// display labels. The pure grouping helper owns ordering, counts and unavailable-label behaviour.
//
// Tokens only (G-181). The board scrolls horizontally inside its own region so the page body never does.

import type { TaskBoardProjection, TaskState } from '../projections.ts';
import { groupTaskBoard, type TaskBoardGrouping } from '../task-board-groups.ts';
import { TaskCard } from './TaskCard.tsx';

export interface TaskBoardProps {
  readonly board: TaskBoardProjection;
  readonly grouping: TaskBoardGrouping;
  /** Which collapsible columns are currently expanded (toolbar col-toggles drive this). */
  readonly expanded: Readonly<Record<string, boolean>>;
  /** The columns that collapse (the prototype's terminal Done/Archived set). */
  readonly collapsible: readonly TaskState[];
}

/** The read-only Task board in its selected state, owner or KPI arrangement. */
export function TaskBoard({
  board,
  grouping,
  expanded,
  collapsible,
}: TaskBoardProps): React.JSX.Element {
  const groups = groupTaskBoard(board, grouping);

  return (
    <div className="board" data-board-grouping={grouping}>
      {groups.length === 0 ? <div className="col-empty">Nothing here</div> : null}
      {groups.map((group) => {
        const state = group.lifecycleState;
        const collapsed =
          state !== null && collapsible.includes(state as TaskState) && !(expanded[state] ?? false);
        return (
          <div
            key={group.key}
            {...(state === null ? {} : { 'data-column': state })}
            data-task-group={grouping}
            role="group"
            aria-label={`${group.label}, ${String(group.count)} tasks`}
            className={collapsed ? 'col col-collapsed' : 'col'}
          >
            <div className="col-head">
              <span className="nm">{group.label}</span>
              <span className="ct">{group.count}</span>
            </div>
            {group.cards.length === 0 ? (
              <div className="col-empty">Nothing here</div>
            ) : (
              group.cards.map((card) => <TaskCard key={card.id} card={card} />)
            )}
          </div>
        );
      })}
    </div>
  );
}
